import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Save, Plus, X, Download, ShieldCheck, Loader2, Bot, Copy, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CompanyProfile() {
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", trade: "peinture", siret: "", phone: "", email: "",
    address: "", postal_code: "", city: "",
    legal_form: "", share_capital: "", rcs_city: "", vat_number: "", ape_code: "",
    insurance_decennale: "",
    rib_bank: "", rib_iban: "", rib_bic: "", rib_holder: "",
    cgv_text: "", cgv_pdf_data: "", cgv_pdf_name: "", quote_footer_notes: "", invoice_footer_notes: "",
    logo_data_uri: "",
    doc_accent_color: "#F28800",
    tax_regime: "IS",
    initial_treasury_balance: "", initial_treasury_date: "",
    initial_accepted_quotes_ttc: "", initial_accepted_quotes_note: "",
    business_mode: "complet",
    vat_franchise: false,
    micro_type: "bic",
    annual_revenue_ht: "",
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Détection auto-entrepreneur : forme juridique micro OU franchise TVA cochée
  const isMicro = form.legal_form === "Micro-entreprise" || form.legal_form === "EI" && form.vat_franchise;
  // Seuils franchise TVA 2026 (à confirmer chaque année)
  const VAT_THRESHOLD = form.micro_type === "bnc" ? 37500 : 85000;
  const MICRO_THRESHOLD = form.micro_type === "bnc" ? 77700 : 188700;

  useEffect(() => {
    api.get("/company").then(({ data }) => {
      if (data) {
        setForm((prev) => ({
          ...prev,
          name: data.name || "",
          trade: data.trade || "peinture",
          siret: data.siret || "",
          phone: data.phone || "",
          email: data.email || "",
          address: data.address || "",
          postal_code: data.postal_code || "",
          city: data.city || "",
          legal_form: data.legal_form || "",
          share_capital: data.share_capital ?? "",
          rcs_city: data.rcs_city || "",
          vat_number: data.vat_number || "",
          ape_code: data.ape_code || "",
          insurance_decennale: data.insurance_decennale || "",
          rib_bank: data.rib_bank || "",
          rib_iban: data.rib_iban || "",
          rib_bic: data.rib_bic || "",
          rib_holder: data.rib_holder || "",
          cgv_text: data.cgv_text || "",
          cgv_pdf_data: data.cgv_pdf_data || "",
          cgv_pdf_name: data.cgv_pdf_name || "",
          quote_footer_notes: data.quote_footer_notes || "",
          invoice_footer_notes: data.invoice_footer_notes || "",
          logo_data_uri: data.logo_data_uri || "",
          doc_accent_color: data.doc_accent_color || "#F28800",
          tax_regime: data.tax_regime || "IS",
          initial_treasury_balance: data.initial_treasury_balance ?? "",
          initial_treasury_date: data.initial_treasury_date || "",
          initial_accepted_quotes_ttc: data.initial_accepted_quotes_ttc ?? "",
          initial_accepted_quotes_note: data.initial_accepted_quotes_note || "",
          business_mode: data.business_mode || "complet",
          vat_franchise: data.vat_franchise ?? false,
          micro_type: data.micro_type || "bic",
          annual_revenue_ht: data.annual_revenue_ht ?? "",
        }));
      }
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company", {
        ...form,
        share_capital: form.share_capital === "" ? null : Number(form.share_capital) || 0,
        initial_treasury_balance: form.initial_treasury_balance === "" ? null : Number(form.initial_treasury_balance) || 0,
        initial_accepted_quotes_ttc: form.initial_accepted_quotes_ttc === "" ? null : Number(form.initial_accepted_quotes_ttc) || 0,
        annual_revenue_ht: form.annual_revenue_ht === "" ? null : Number(form.annual_revenue_ht) || 0,
        vat_franchise: !!form.vat_franchise,
      });
      toast.success("Profil entreprise enregistré");
      try { await refresh(); } catch {/* noop */}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 300 * 1024) { toast.error("Logo trop volumineux (max 300 Ko)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm({ ...form, logo_data_uri: ev.target.result });
    reader.readAsDataURL(f);
  };

  if (!loaded) return <div className="text-tertiary-text flex items-center gap-2"><span className="spinner" /> Chargement…</div>;

  return (
    <div data-testid="profile-page" className="max-w-5xl mx-auto">
      <header className="mb-10 animate-in">
        <div className="flex items-center gap-2 font-body text-xs font-semibold mb-3" style={{ color: "#FFC400" }}>
          <Building2 size={12} strokeWidth={2} /> Profil entreprise
        </div>
        <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
          Votre <span className="font-display" style={{ color: "#FFC400" }}>identité</span>
        </h1>
        <p className="text-secondary-text mt-3 max-w-3xl font-body text-[15px]">
          Toutes les informations légales et bancaires de votre entreprise. Apparaissent sur vos devis, factures et fiches chantier PDF.
        </p>
      </header>

      {/* Mode d'activité */}
      <section className="card p-6 mb-6 animate-in" data-testid="business-mode-card">
        <h2 className="font-display text-xl tracking-tight mb-1" style={{ fontWeight: 600 }}>Mode d'activité</h2>
        <p className="text-xs text-tertiary-text font-body mb-5">
          Définit le périmètre fonctionnel de PitBoard pour votre entreprise. Vous pouvez changer à tout moment.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { k: "complet", label: "Mode Main d'œuvre + matériel", desc: "Devis, factures, matériel à commander, fiches chantier, ouvrages avec matières et MO." },
            { k: "prestation", label: "Prestation de service pure", desc: "Devis et factures de services uniquement. Pas de matériel ni fiches chantier — focus 360° gestion." },
          ].map(({ k, label, desc }) => {
            const active = form.business_mode === k;
            return (
              <button
                key={k}
                onClick={() => setForm({ ...form, business_mode: k })}
                className="text-left p-4 rounded-lg border transition-all"
                style={{
                  background: active ? "#FFC400" : "#161618",
                  color: active ? "#161618" : "#F5F4F2",
                  borderColor: active ? "#FFC400" : "#3A3D42",
                }}
                data-testid={`mode-${k}`}
              >
                <div className="font-display text-base mb-1" style={{ fontWeight: 600 }}>{label}</div>
                <div className="text-[12px] opacity-80 font-body leading-snug">{desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Coordonnées de base */}
      <section className="card p-6 mb-6 animate-in" data-testid="profile-basic">
        <h2 className="font-display text-xl tracking-tight mb-5" style={{ fontWeight: 600 }}>Coordonnées</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Nom de l'entreprise" testid="company-name">
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Métier" testid="company-trade">
            <select className="input" value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })}>
              <option value="peinture">Peinture</option>
              <option value="plomberie">Plomberie</option>
              <option value="electricite">Électricité</option>
              <option value="menuiserie">Menuiserie</option>
              <option value="macon">Maçonnerie</option>
              <option value="multi-corps">Multi-corps d'état</option>
              <option value="autre">Autre</option>
            </select>
          </Field>
          <Field label="SIRET" testid="company-siret">
            <input className="input numerals" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
          </Field>
          <Field label="Téléphone" testid="company-phone">
            <input className="input numerals" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Adresse complète" testid="company-address" full>
            <input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="Code postal" testid="company-postal">
            <input className="input numerals" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
          </Field>
          <Field label="Ville" testid="company-city">
            <input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </Field>
          <Field label="Email" testid="company-email">
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
        </div>
      </section>

      {/* Logo */}
      <section className="card p-6 mb-6 animate-in delay-1">
        <h2 className="font-display text-xl tracking-tight mb-5" style={{ fontWeight: 600 }}>Logo de l'entreprise</h2>
        <div className="flex items-center gap-5">
          {form.logo_data_uri ? (
            <img src={form.logo_data_uri} alt="Logo" className="h-24 max-w-[260px] object-contain border bg-[var(--surface)] p-2 rounded-md" style={{ borderColor: "#3A3D42" }} />
          ) : (
            <div className="h-24 w-24 border-2 border-dashed flex items-center justify-center text-tertiary-text text-xs rounded-md" style={{ borderColor: "#3A3D42" }}>Pas de logo</div>
          )}
          <div className="flex flex-col gap-2">
            <label className="btn btn-outline btn-sm cursor-pointer">
              <Plus size={13} /> Choisir un fichier
              <input type="file" accept="image/*" className="hidden" onChange={handleLogo} data-testid="logo-upload" />
            </label>
            {form.logo_data_uri && (
              <button onClick={() => setForm({ ...form, logo_data_uri: "" })} className="btn btn-ghost btn-sm" style={{ color: "#E10600" }}><X size={13} /> Retirer</button>
            )}
            <span className="text-[11px] text-[var(--grey)]">PNG/JPG · max 300 Ko</span>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t" style={{ borderColor: "#3A3D42" }}>
          <div className="text-sm font-semibold mb-1">Couleur de vos documents (devis PDF)</div>
          <p className="text-xs text-tertiary-text font-body mb-3">
            Cette couleur personnalise vos devis : bandeau, en-têtes du tableau et totaux. Elle n&apos;affecte pas l&apos;application.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.doc_accent_color || "#F28800"}
              onChange={(e) => setForm({ ...form, doc_accent_color: e.target.value })}
              className="h-10 w-16 rounded cursor-pointer border"
              style={{ borderColor: "#3A3D42", background: "transparent" }}
              data-testid="doc-accent-color-input"
            />
            <code className="text-xs px-2 py-1 rounded" style={{ background: "rgba(0,0,0,0.4)" }}>{form.doc_accent_color || "#F28800"}</code>
            <button onClick={() => setForm({ ...form, doc_accent_color: "#F28800" })} className="btn btn-ghost btn-sm" data-testid="doc-accent-reset-btn">
              Orange flamme (défaut)
            </button>
          </div>
        </div>
      </section>

      {/* Mentions légales */}
      <section className="card p-6 mb-6 animate-in delay-2">
        <h2 className="font-display text-xl tracking-tight mb-1" style={{ fontWeight: 600 }}>Mentions légales</h2>
        <p className="text-xs text-tertiary-text font-body mb-5">Obligatoires en pied de page de chaque facture / devis.</p>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Forme juridique" testid="legal-form">
            <select className="input" value={form.legal_form} onChange={(e) => {
              const lf = e.target.value;
              // Pré-cocher franchise TVA + régime micro quand on choisit Micro-entreprise
              const isM = lf === "Micro-entreprise";
              setForm({ ...form, legal_form: lf,
                vat_franchise: isM ? true : form.vat_franchise,
                tax_regime: isM ? "micro" : form.tax_regime,
                share_capital: isM ? "" : form.share_capital,
              });
            }}>
              <option value="">—</option>
              <option value="Micro-entreprise">Micro-entreprise / Auto-entrepreneur</option>
              <option value="EI">Entreprise individuelle (EI au réel)</option>
              <option value="EURL">EURL</option>
              <option value="SARL">SARL</option>
              <option value="SAS">SAS</option>
              <option value="SASU">SASU</option>
              <option value="SA">SA</option>
            </select>
          </Field>

          <Field label="Régime fiscal" testid="legal-tax-regime">
            <select className="input" value={form.tax_regime} onChange={(e) => setForm({ ...form, tax_regime: e.target.value })}>
              <option value="IS">Impôt sur les sociétés (IS)</option>
              <option value="IR">Impôt sur le revenu (IR)</option>
              <option value="micro">Micro-fiscal / Auto-entrepreneur</option>
            </select>
          </Field>

          {/* Bloc spécifique Auto-entrepreneur */}
          {isMicro && (
            <div className="md:col-span-2 rounded-lg p-4" style={{ background: "rgba(255,196,0,0.08)", border: "1px solid rgba(255,196,0,0.30)" }} data-testid="auto-entrepreneur-block">
              <div className="flex items-center gap-2 mb-3">
                <span className="font-display text-sm" style={{ color: "#FFC400", fontWeight: 600 }}>
                  Régime auto-entrepreneur / micro-entreprise
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Type d'activité (micro)" testid="micro-type">
                  <select className="input" value={form.micro_type} onChange={(e) => setForm({ ...form, micro_type: e.target.value })}>
                    <option value="bic">BIC — Entrepreneur / Commerçant (BTP, vente, fabrication)</option>
                    <option value="bnc">BNC — Profession libérale / Prestation intellectuelle</option>
                  </select>
                </Field>
                <Field label={`Chiffre d'affaires annuel HT en cours (€)`} testid="annual-revenue">
                  <input
                    type="number" step="0.01" className="input numerals"
                    value={form.annual_revenue_ht}
                    onChange={(e) => setForm({ ...form, annual_revenue_ht: e.target.value })}
                    placeholder="0"
                  />
                  {Number(form.annual_revenue_ht) > 0 && (
                    <div className="text-[11px] mt-1.5 flex flex-col gap-0.5">
                      <span style={{ color: Number(form.annual_revenue_ht) > VAT_THRESHOLD ? "#E10600" : "var(--grey)" }}>
                        Seuil franchise TVA ({form.micro_type === "bnc" ? "BNC" : "BIC"}) : {VAT_THRESHOLD.toLocaleString("fr-FR")} €
                        {Number(form.annual_revenue_ht) > VAT_THRESHOLD && " — DÉPASSÉ : TVA obligatoire"}
                      </span>
                      <span style={{ color: Number(form.annual_revenue_ht) > MICRO_THRESHOLD ? "#E10600" : "var(--grey)" }}>
                        Seuil régime micro : {MICRO_THRESHOLD.toLocaleString("fr-FR")} €
                        {Number(form.annual_revenue_ht) > MICRO_THRESHOLD && " — DÉPASSÉ : bascule au réel"}
                      </span>
                    </div>
                  )}
                </Field>
                <Field label="Franchise TVA" testid="vat-franchise" full>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.vat_franchise}
                      onChange={(e) => setForm({ ...form, vat_franchise: e.target.checked })}
                      className="w-4 h-4 accent-[#FFC400]"
                    />
                    <span className="text-sm" style={{ color: "var(--chalk)" }}>
                      Bénéficie de la franchise TVA (art. 293 B du CGI)
                    </span>
                  </label>
                  <div className="text-[11px] text-[var(--grey)] mt-1.5">
                    Si activé : aucune TVA facturée. La mention "TVA non applicable, art. 293 B du CGI" sera automatiquement ajoutée sur tous vos devis et factures.
                  </div>
                </Field>
              </div>
            </div>
          )}

          {/* Capital social — masqué pour Micro */}
          {form.legal_form !== "Micro-entreprise" && (
            <Field label="Capital social (€)" testid="legal-capital">
              <input type="number" className="input numerals" value={form.share_capital} onChange={(e) => setForm({ ...form, share_capital: e.target.value })} />
            </Field>
          )}

          {/* N° TVA intra — masqué si franchise TVA */}
          {!form.vat_franchise && (
            <Field label="N° TVA intracommunautaire" testid="legal-tva">
              <input className="input numerals" placeholder="FRXX XXXXXXXXX" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
            </Field>
          )}

          <Field label="Code APE / NAF" testid="legal-ape">
            <input className="input numerals" placeholder="4334Z" value={form.ape_code} onChange={(e) => setForm({ ...form, ape_code: e.target.value })} />
          </Field>

          <Field label={form.legal_form === "Micro-entreprise" ? "RM / RCS (immatriculation)" : "Ville d'immatriculation RCS"} testid="legal-rcs">
            <input className="input" placeholder={form.legal_form === "Micro-entreprise" ? "RM Paris (entrepreneur) ou RCS Paris" : "RCS Paris"} value={form.rcs_city} onChange={(e) => setForm({ ...form, rcs_city: e.target.value })} />
          </Field>

          <Field label="Assurance décennale" testid="legal-insurance" full={form.legal_form === "Micro-entreprise"}>
            <input className="input" placeholder="MMA, n° 1234567" value={form.insurance_decennale} onChange={(e) => setForm({ ...form, insurance_decennale: e.target.value })} />
          </Field>
        </div>
      </section>

      {/* RIB */}
      <section className="card p-6 mb-6 animate-in delay-3">
        <h2 className="font-display text-xl tracking-tight mb-1" style={{ fontWeight: 600 }}>Coordonnées bancaires (RIB)</h2>
        <p className="text-xs text-tertiary-text font-body mb-5">Apparaîtront automatiquement sur vos factures.</p>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Titulaire du compte" testid="rib-holder">
            <input className="input" value={form.rib_holder} onChange={(e) => setForm({ ...form, rib_holder: e.target.value })} />
          </Field>
          <Field label="Banque" testid="rib-bank">
            <input className="input" value={form.rib_bank} onChange={(e) => setForm({ ...form, rib_bank: e.target.value })} />
          </Field>
          <Field label="IBAN" testid="rib-iban" full>
            <input className="input numerals uppercase" placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" value={form.rib_iban} onChange={(e) => setForm({ ...form, rib_iban: e.target.value })} />
          </Field>
          <Field label="BIC / SWIFT" testid="rib-bic">
            <input className="input numerals uppercase" value={form.rib_bic} onChange={(e) => setForm({ ...form, rib_bic: e.target.value })} />
          </Field>
        </div>
      </section>

      {/* Solde de démarrage */}
      <section className="card p-6 mb-6 animate-in delay-3">
        <h2 className="font-display text-xl tracking-tight mb-1" style={{ fontWeight: 600 }}>Solde bancaire de démarrage</h2>
        <p className="text-xs text-tertiary-text font-body mb-5">
          Si votre entreprise était déjà en activité avant d'utiliser PitBoard, saisissez le solde en banque au moment du démarrage.
          Ce montant s'ajoutera à la trésorerie calculée par PitBoard.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Solde bancaire initial (€)" testid="initial-balance">
            <input type="number" step="0.01" className="input numerals" placeholder="Ex: 12500" value={form.initial_treasury_balance} onChange={(e) => setForm({ ...form, initial_treasury_balance: e.target.value })} />
          </Field>
          <Field label="Date du solde" testid="initial-balance-date">
            <input type="date" className="input" value={form.initial_treasury_date || ""} onChange={(e) => setForm({ ...form, initial_treasury_date: e.target.value })} />
          </Field>
        </div>

        <div className="mt-6 pt-5 border-t border-[var(--border)]">
          <h3 className="font-display text-base mb-1" style={{ fontWeight: 600 }}>Carnet de commande de reprise</h3>
          <p className="text-xs text-tertiary-text font-body mb-4">
            Si vous avez déjà des devis acceptés <strong>en cours d&apos;exécution</strong> non encore saisis dans PitBoard, indiquez ici leur montant total.
            Vos indicateurs « Devis signés » et trésorerie prévisionnelle en tiendront compte automatiquement.
            {form.vat_franchise && (
              <> En franchise TVA (auto-entreprise), HT = TTC.</>
            )}
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={`Devis acceptés à reprendre (€ ${form.vat_franchise ? "TTC" : "HT"})`} testid="initial-backlog-ttc">
              <input
                type="number" step="0.01" className="input numerals" placeholder="Ex: 28500"
                value={form.initial_accepted_quotes_ttc ?? ""}
                onChange={(e) => setForm({ ...form, initial_accepted_quotes_ttc: e.target.value })}
              />
            </Field>
            <Field label="Note (optionnel)" testid="initial-backlog-note">
              <input
                type="text" className="input" placeholder="Ex: 3 chantiers en cours (Dupont, Martin, Bertrand)"
                value={form.initial_accepted_quotes_note ?? ""}
                onChange={(e) => setForm({ ...form, initial_accepted_quotes_note: e.target.value })}
              />
            </Field>
          </div>
        </div>
      </section>

      {/* CGV */}
      <section className="card p-6 mb-6 animate-in delay-4">
        <h2 className="font-display text-xl tracking-tight mb-1" style={{ fontWeight: 600 }}>Conditions Générales de Vente (CGV)</h2>
        <p className="text-xs text-tertiary-text font-body mb-4">
          <b>Recommandé :</b> importez vos CGV en PDF — elles seront ajoutées telles quelles en annexe de chaque devis et facture (mise en page conservée, pas de pages regénérées).
        </p>

        <div className="rounded-lg p-4 mb-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid #3A3D42" }}>
          <div className="text-sm font-semibold mb-2">CGV en PDF (prioritaire)</div>
          {form.cgv_pdf_name ? (
            <div className="flex items-center gap-3 flex-wrap" data-testid="cgv-pdf-info">
              <span className="text-sm">📄 <b>{form.cgv_pdf_name}</b></span>
              <button
                onClick={() => setForm({ ...form, cgv_pdf_data: "", cgv_pdf_name: "" })}
                className="btn btn-outline" style={{ padding: "5px 12px", fontSize: 13 }}
                data-testid="cgv-pdf-remove-btn"
              >
                <X size={13} /> Retirer
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="file"
                accept="application/pdf"
                data-testid="cgv-pdf-upload"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 2 * 1024 * 1024) { toast.error("PDF trop lourd (max 2 Mo)."); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm({ ...form, cgv_pdf_data: reader.result, cgv_pdf_name: f.name });
                  reader.readAsDataURL(f);
                }}
                className="text-sm"
              />
              <span className="text-[11px] text-[var(--grey)]">PDF uniquement · max 2 Mo</span>
            </div>
          )}
        </div>

        <div className="text-sm font-semibold mb-1">Ou CGV en texte {form.cgv_pdf_name ? <span className="text-[11px] font-normal" style={{ color: "var(--grey)" }}>(ignoré tant qu'un PDF est importé)</span> : null}</div>
        <textarea
          className="input min-h-[200px] font-body"
          rows={10}
          placeholder="Article 1 — Objet&#10;Les présentes conditions générales de vente régissent...&#10;&#10;Article 2 — Prix&#10;..."
          value={form.cgv_text}
          onChange={(e) => setForm({ ...form, cgv_text: e.target.value })}
          data-testid="cgv-text"
        />
      </section>

      {/* Footer notes */}
      <section className="card p-6 mb-6 animate-in delay-4">
        <h2 className="font-display text-xl tracking-tight mb-1" style={{ fontWeight: 600 }}>Mentions de pied de page</h2>
        <p className="text-xs text-tertiary-text font-body mb-5">Apparaîtront sur les devis et factures.</p>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Note de pied de devis (modalités)" testid="quote-footer">
            <textarea className="input min-h-[100px]" rows={4}
              placeholder="Ex: 30% à la commande, 70% à la fin de chantier. Validité 30 jours."
              value={form.quote_footer_notes}
              onChange={(e) => setForm({ ...form, quote_footer_notes: e.target.value })} />
          </Field>
          <Field label="Note de pied de facture" testid="invoice-footer">
            <textarea className="input min-h-[100px]" rows={4}
              placeholder="Ex: Paiement à 30 jours date de facture. TVA acquittée sur les encaissements."
              value={form.invoice_footer_notes}
              onChange={(e) => setForm({ ...form, invoice_footer_notes: e.target.value })} />
          </Field>
        </div>
      </section>

      {/* Export RGPD — Portabilité et sauvegarde utilisateur */}
      <DataExportSection />

      {/* Connexion assistant IA (Limova / Charly) */}
      <CharlyIntegrationSection />

      {/* Sticky save bar */}
      <div className="sticky bottom-0 mt-8 flex justify-end gap-2 p-4 -mx-4 sm:-mx-6 border-t" style={{ background: "rgba(11, 11, 12, 0.92)", borderColor: "#3A3D42", backdropFilter: "blur(8px)" }}>
        <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-profile">
          <Save size={14} /> {saving ? "Enregistrement..." : "Enregistrer le profil"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children, testid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function DataExportSection() {
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/data-export/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/data-export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : "pitboard-export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé. Conservez-le en lieu sûr.");
      // Refresh status
      api.get("/data-export/status").then(({ data }) => setStatus(data)).catch(() => {});
    } catch {
      toast.error("Erreur pendant l'export. Réessayez.");
    } finally {
      setDownloading(false);
    }
  };

  const days = status?.days_since_last_export;
  const staleWarning = days === null || days >= 30;

  return (
    <section className="card p-6 mb-6 animate-in delay-4" data-testid="data-export-section">
      <div className="flex items-start gap-3 mb-2">
        <ShieldCheck size={20} style={{ color: "var(--pit-yellow)", marginTop: 2 }} />
        <div className="flex-1">
          <h2 className="font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
            Export de mes données (RGPD)
          </h2>
          <p className="text-xs text-tertiary-text font-body mt-1">
            Téléchargez un ZIP complet de toutes vos données PitBoard (devis, factures, clients, bibliothèque…).
            Conservez-le en lieu sûr — c&apos;est votre sauvegarde perso, indépendante de PitBoard.
          </p>
        </div>
      </div>
      <div
        className="rounded-lg p-3 mb-4 text-[12px] flex items-start gap-2"
        style={{
          background: staleWarning ? "rgba(225,6,0,0.08)" : "rgba(34,197,94,0.08)",
          border: `1px solid ${staleWarning ? "rgba(225,6,0,0.35)" : "rgba(34,197,94,0.35)"}`,
          color: staleWarning ? "#E10600" : "#22C55E",
        }}
      >
        {status === null ? (
          <span>Chargement…</span>
        ) : days === null ? (
          <span><b>Aucun export réalisé pour l&apos;instant.</b> Nous vous recommandons d&apos;en faire un dès maintenant.</span>
        ) : days >= 30 ? (
          <span>Dernier export il y a <b>{days} jours</b>. Un nouvel export est recommandé (tous les 30 jours).</span>
        ) : (
          <span>Dernier export il y a <b>{days} jour{days > 1 ? "s" : ""}</b>. À jour ✓</span>
        )}
      </div>
      <button
        onClick={download}
        disabled={downloading}
        className="btn btn-primary"
        data-testid="btn-download-export"
      >
        {downloading ? (
          <><Loader2 size={14} className="animate-spin" /> Génération…</>
        ) : (
          <><Download size={14} /> Télécharger mes données (ZIP)</>
        )}
      </button>
      <p className="text-[11px] mt-3" style={{ color: "var(--grey)" }}>
        Un rappel automatique par email vous est envoyé tous les 30 jours pour vous inciter à conserver une copie locale.
        Voir <a href="/cgv" style={{ color: "var(--pit-yellow)" }} target="_blank" rel="noreferrer">CGV article 11.4 — Continuité et sauvegarde</a>.
      </p>
    </section>
  );
}

function CharlyIntegrationSection() {
  const [status, setStatus] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api/ext/v1`;

  const load = () =>
    api.get("/integrations/apikey")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ forbidden: true }));
  useEffect(() => { load(); }, []);

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copié !`);
  };

  const generate = async () => {
    if (status?.exists && !window.confirm("Générer une nouvelle clé révoquera l'ancienne — Charly devra être reconfiguré. Continuer ?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/apikey");
      setNewKey(data.api_key);
      load();
      toast.success("Clé générée. Copiez-la maintenant : elle ne sera plus jamais affichée.");
    } catch {
      toast.error("Erreur lors de la génération de la clé.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Révoquer la clé ? Charly perdra l'accès immédiatement.")) return;
    try {
      await api.delete("/integrations/apikey");
      setNewKey(null);
      load();
      toast.success("Clé révoquée.");
    } catch {
      toast.error("Erreur lors de la révocation.");
    }
  };

  if (status?.forbidden) return null;

  return (
    <section className="card p-6 mb-6 animate-in delay-4" data-testid="charly-integration-section">
      <div className="flex items-start gap-3 mb-4">
        <Bot size={20} style={{ color: "var(--pit-yellow)", marginTop: 2 }} />
        <div className="flex-1">
          <h2 className="font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
            Connecter un assistant IA (Limova / Charly)
          </h2>
          <p className="text-xs text-tertiary-text font-body mt-1">
            Générez une clé API pour que votre assistant IA consulte et agisse dans PitBoard :
            suivi des devis et impayés, création de clients et devis, relances… par simple message ou vocal.
          </p>
        </div>
      </div>

      {newKey && (
        <div className="rounded-lg p-4 mb-4" data-testid="new-api-key-block"
             style={{ background: "rgba(255,196,0,0.08)", border: "1px solid rgba(255,196,0,0.35)" }}>
          <div className="text-xs font-semibold mb-2" style={{ color: "var(--pit-yellow)" }}>
            ⚠️ Copiez cette clé MAINTENANT — elle ne sera plus jamais affichée :
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs px-2 py-1.5 rounded break-all" style={{ background: "rgba(0,0,0,0.4)", color: "var(--chalk)" }}>{newKey}</code>
            <button onClick={() => copy(newKey, "Clé API")} className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 13 }} data-testid="copy-api-key-btn">
              <Copy size={13} /> Copier
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg p-3 mb-4 text-[12px]" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid #3A3D42" }}>
        {status === null ? (
          <span>Chargement…</span>
        ) : status.exists ? (
          <span data-testid="api-key-status">
            🔑 Clé active : <b>{status.prefix}…</b> · créée le {status.created_at ? new Date(status.created_at).toLocaleDateString("fr-FR") : "—"}
            {status.last_used_at
              ? <> · dernière utilisation le {new Date(status.last_used_at).toLocaleDateString("fr-FR")}</>
              : <> · <span style={{ color: "var(--grey)" }}>jamais utilisée</span></>}
          </span>
        ) : (
          <span data-testid="api-key-status">Aucune clé active. Générez-en une pour connecter votre assistant.</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={generate} disabled={busy} className="btn btn-primary" data-testid="generate-api-key-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
          {status?.exists ? "Régénérer la clé" : "Générer ma clé API"}
        </button>
        {status?.exists && (
          <button onClick={revoke} className="btn btn-outline" data-testid="revoke-api-key-btn">
            <Trash2 size={14} /> Révoquer
          </button>
        )}
      </div>

      <div className="text-[12px] space-y-1.5" style={{ color: "var(--grey)" }}>
        <div className="font-semibold" style={{ color: "var(--chalk)" }}>Comment connecter Charly (Limova) :</div>
        <div>1. Générez votre clé API ci-dessus et copiez-la.</div>
        <div className="flex items-center gap-2 flex-wrap">
          <span>2. Adresse de l&apos;API à indiquer :</span>
          <code className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.4)" }}>{apiBase}</code>
          <button onClick={() => copy(apiBase, "Adresse API")} className="underline" style={{ color: "var(--pit-yellow)" }} data-testid="copy-api-base-btn">copier</button>
        </div>
        <div>3. Dans Limova → Intégrations → connexion API personnalisée : collez l&apos;adresse et la clé (header <code>X-API-Key</code>).</div>
        <div>4. Demandez à Charly : « Fais-moi le point sur mes devis en cours et mes impayés » 🏁</div>
      </div>
    </section>
  );
}
